import { useState, useRef, useEffect, useCallback } from 'react';
import * as mediasoupClient from 'mediasoup-client';

export function useWebRTC({ localStream, send, onSignaling, onReady, wsConnected }) {
  const [localProducerId, setLocalProducerId] = useState(null);
  const [deviceReady, setDeviceReady] = useState(false);

  const sendRef = useRef(send);
  sendRef.current = send;
  const onSignalingRef = useRef(onSignaling);
  onSignalingRef.current = onSignaling;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const deviceRef = useRef(null);              // mediasoup-client Device
  const sendTransportRef = useRef(null);       // sendTransport
  const sendTransportPromiseRef = useRef(null);
  const recvTransportRef = useRef(null);       // recvTransport
  // A peer needs exactly one recv transport for all of its consumers. Several
  // producers can arrive at once, so the in-flight creation is memoised as a
  // promise — otherwise parallel subscribes each build their own transport and
  // all but the last get orphaned on the SFU.
  const recvTransportPromiseRef = useRef(null);
  const producersRef = useRef(new Map());      // key: `${kind}:${track.id}` -> producer
  const consumersRef = useRef(new Map());      // consumerId -> consumer

  // Очередь ожидания сигналов (как у тебя в исходном коде)
  const waitersRef = useRef([]);

  const signal = useCallback((msg) => {
    console.log('[WEBRTC] signaling message received', msg.type, JSON.stringify(msg.payload || {}).slice(0, 200));
    for (let i = 0; i < waitersRef.current.length; i++) {
      try {
        if (waitersRef.current[i](msg)) {
          waitersRef.current.splice(i, 1);
          return;
        }
      } catch {
        // оставляем остальные waiters
      }
    }
    console.log('[WEBRTC] message consumed by no waiter', msg.type);
  }, []);

  function wait(fn, timeoutMs) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        const idx = waitersRef.current.indexOf(fn);
        if (idx !== -1) waitersRef.current.splice(idx, 1);
        reject(new Error(`timed out after ${timeoutMs}ms`));
      }, timeoutMs);

      const predicate = (msg) => {
        try {
          if (fn(msg)) {
            clearTimeout(timer);
            resolve();
            return true;
          }
        } catch (err) {
          clearTimeout(timer);
          reject(err);
          return true;
        }
        return false;
      };

      waitersRef.current.push(predicate);
    });
  }

  /**
   * 1. Запросить routerRtpCapabilities
   * 2. Создать Device и загрузить capabilities
   * 3. Отправить setRtpCapabilities и запросить getProducers
   */
  const setupDeviceAndTransports = useCallback(async () => {
    if (!wsConnected) throw new Error('WebSocket not connected');

    console.log('[WEBRTC] === setupDeviceAndTransports START ===');

    // 1) routerRtpCapabilities
    sendRef.current({ type: 'getRouterRtpCapabilities' });
    console.log('[WEBRTC] sent getRouterRtpCapabilities');

    let routerRtpCaps = null;
    await wait((msg) => {
      if (msg.type === 'routerRtpCapabilities') {
        routerRtpCaps = msg.payload || msg.data;
        console.log('[WEBRTC] routerRtpCapabilities received', JSON.stringify(routerRtpCaps).slice(0, 300));
        return true;
      }
      if (msg.type === 'error') {
        throw new Error((msg.payload || {}).message || msg.error || 'failed to get routerRtpCapabilities');
      }
      return false;
    }, 15000);

    // 2) Device
    console.log('[WEBRTC] creating mediasoup-client Device');
    const device = new mediasoupClient.Device();
    await device.load({ routerRtpCapabilities: routerRtpCaps.rtpCapabilities });

    console.log('[WEBRTC] device loaded, rtpCapabilities:', JSON.stringify(device.rtpCapabilities).slice(0, 500));
    console.log('[WEBRTC] canProduce video:', device.canProduce('video'));

    deviceRef.current = device;
    setDeviceReady(true);
    if (onSignalingRef.current) onSignalingRef.current('deviceReady', {});

    sendRef.current({
      type: 'setRtpCapabilities',
      payload: device.rtpCapabilities,
    });
    console.log('[WEBRTC] sent setRtpCapabilities');

    // Peers already streaming when we joined produced before we could hear the
    // broadcast, so ask for the current list explicitly.
    sendRef.current({ type: 'getProducers' });
    console.log('[WEBRTC] sent getProducers');
  }, [wsConnected]);

  /**
   * Создать (один раз) sendTransport на клиенте и сервере.
   */
  const ensureSendTransport = useCallback(() => {
    if (sendTransportRef.current) return Promise.resolve(sendTransportRef.current);
    if (sendTransportPromiseRef.current) return sendTransportPromiseRef.current;

    const promise = (async () => {
      if (!deviceRef.current) throw new Error('Device not initialized');

      console.log('[WEBRTC] requesting sendTransport from server');
      sendRef.current({ type: 'createWebRtcTransport', payload: { direction: 'send' } });

      let sendTransportParams = null;
      await wait((msg) => {
        if (msg.type === 'transportCreated') {
          const data = msg.payload || msg.data || {};
          if (data.direction && data.direction !== 'send') return false;
          sendTransportParams = {
            transportId: data.id || '',
            iceParameters: data.iceParameters || {},
            iceCandidates: data.iceCandidates || [],
            dtlsParameters: data.dtlsParameters || {},
            direction: 'send',
          };
          console.log('[WEBRTC] sendTransport server params received, transportId:', data.id);
          return true;
        }
        if (msg.type === 'error') {
          throw new Error((msg.payload || {}).message || msg.error || 'createWebRtcTransport (send) failed');
        }
        return false;
      }, 15000);

      console.log('[WEBRTC] creating client-side sendTransport');
      const sendTransport = deviceRef.current.createSendTransport({
        id: sendTransportParams.transportId,
        iceParameters: sendTransportParams.iceParameters,
        iceCandidates: sendTransportParams.iceCandidates,
        dtlsParameters: sendTransportParams.dtlsParameters,
        appData: { direction: 'send' },
      });

      console.log('[WEBRTC] sendTransport created on client, id:', sendTransport.id);

      sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
        console.log('[WEBRTC] sendTransport connect event fired — sending dtlsParameters');

        sendRef.current({
          type: 'connectWebRtcTransport',
          payload: { transportId: sendTransportParams.transportId, dtlsParameters },
        });

        wait((msg) => {
          if (msg.type === 'transportConnected') {
            console.log('[WEBRTC] sendTransport connected');
            callback();
            return true;
          }
          if (msg.type === 'error') {
            errback(new Error((msg.payload || {}).message || msg.error || 'connectWebRtcTransport failed'));
            return true;
          }
          return false;
        }, 15000).catch(errback);
      });

      sendTransport.on('connectionstatechange', (state) => {
        console.log('[WEBRTC] sendTransport connectionstatechange:', state);
        if (state === 'failed') {
          onSignalingRef.current?.('error', {
            message: 'ICE не установился (send) — проверьте ANNOUNCED_IP и проброс UDP-портов SFU',
          });
        }
      });

      sendTransport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
        console.log('[WEBRTC] produce event fired — kind:', kind, 'rtpParameters present:', !!rtpParameters);
        sendRef.current({
          type: 'produce',
          payload: { kind, rtpParameters, appData },
        });

        wait((msg) => {
          if (msg.type === 'producer') {
            const data = msg.payload || msg.data;
            if (data && data.producerId) {
              console.log('[WEBRTC] producer created on server, producerId:', data.producerId);
              callback({ id: data.producerId });
              setLocalProducerId(data.producerId);
              if (onSignalingRef.current) onSignalingRef.current('produced', data);
              if (onReadyRef.current) onReadyRef.current();
              return true;
            }
          }
          if (msg.type === 'error') {
            errback(new Error((msg.payload || {}).message || msg.error || 'produce failed'));
            return true;
          }
          return false;
        }, 15000).catch(errback);
      });

      sendTransportRef.current = sendTransport;
      if (onSignalingRef.current) onSignalingRef.current('transportCreated', sendTransportParams);
      return sendTransport;
    })();

    sendTransportPromiseRef.current = promise;
    promise.catch(() => { sendTransportPromiseRef.current = null; });
    return promise;
  }, []);

  /**
   * Produce: отправить все треки localStream через mediasoup-client
   * Если localStream === null — просто ничего не делаем (слушатель).
   */
  const produce = useCallback(async (stream) => {
    if (!stream) {
      console.log('[WEBRTC] produce skipped: stream is null (listener mode)');
      return;
    }

    if (!deviceRef.current) {
      console.log('[WEBRTC] produce skipped: device not ready yet');
      return;
    }

    const transport = await ensureSendTransport();

    console.log('[WEBRTC] === PRODUCE START === tracks:', stream.getTracks().map((t) => `${t.kind}:${t.label.slice(0, 20)}`));

    const videoTracks = stream.getVideoTracks();
    const audioTracks = stream.getAudioTracks();

    const videoTrack = videoTracks[0];
    const audioTrack = audioTracks[0];

    // Видео
    if (videoTrack) {
      try {
        const key = `video:${videoTrack.id}`;
        console.log('[WEBRTC] producing video track, key:', key);

        let existingVideoProducer = null;
        for (const [k, p] of producersRef.current.entries()) {
          if (k.startsWith('video:') && p && typeof p.replaceTrack === 'function') {
            existingVideoProducer = p;
            break;
          }
        }

        if (existingVideoProducer) {
          if (!producersRef.current.has(key)) {
            console.log('[WEBRTC] replacing track on existing video producer');
            await existingVideoProducer.replaceTrack({ track: videoTrack });
            for (const [k] of producersRef.current.entries()) {
              if (k.startsWith('video:')) producersRef.current.delete(k);
            }
            producersRef.current.set(key, existingVideoProducer);
            console.log('[WEBRTC] video track replaced successfully');
          } else {
            console.log('[WEBRTC] video already produced, skipping');
          }
        } else if (!producersRef.current.has(key)) {
          producersRef.current.set(key, 'pending');
          const videoProducer = await transport.produce({ track: videoTrack });
          producersRef.current.set(key, videoProducer);
          console.log('[WEBRTC] video produced successfully, producerId:', videoProducer.id);

          videoProducer.on('trackended', () => {
            console.log('[WEBRTC] video track ended');
            videoProducer.close();
            producersRef.current.delete(key);
          });

          videoProducer.on('transportclose', () => {
            producersRef.current.delete(key);
          });
        } else {
          console.log('[WEBRTC] video already produced, skipping');
        }
      } catch (err) {
        producersRef.current.delete(`video:${videoTrack.id}`);
        console.error('[WEBRTC] cannot produce video', err);
        onSignalingRef.current?.('error', {
          message: `cannot produce video: ${err.message}`,
        });
      }
    } else {
      console.log('[WEBRTC] no video track in stream, skipping video produce');
    }

    // Аудио
    if (audioTrack) {
      try {
        const key = `audio:${audioTrack.id}`;
        console.log('[WEBRTC] producing audio track, key:', key);
        if (!producersRef.current.has(key)) {
          producersRef.current.set(key, 'pending');
          const audioProducer = await transport.produce({ track: audioTrack });
          producersRef.current.set(key, audioProducer);
          console.log('[WEBRTC] audio produced successfully, producerId:', audioProducer.id);

          audioProducer.on('trackended', () => {
            console.log('[WEBRTC] audio track ended');
            audioProducer.close();
            producersRef.current.delete(key);
          });

          audioProducer.on('transportclose', () => {
            producersRef.current.delete(key);
          });
        } else {
          console.log('[WEBRTC] audio already produced, skipping');
        }
      } catch (err) {
        producersRef.current.delete(`audio:${audioTrack.id}`);
        console.error('[WEBRTC] cannot produce audio', err);
        onSignalingRef.current?.('error', {
          message: `cannot produce audio: ${err.message}`,
        });
      }
    } else {
      console.log('[WEBRTC] no audio track in stream, skipping audio produce');
    }

    console.log('[WEBRTC] === PRODUCE DONE === producers live:', producersRef.current.size);
  }, [ensureSendTransport]);

  /**
   * Создать (один раз) recvTransport. Параллельные вызовы получают один и тот
   * же промис, поэтому транспорт на SFU создаётся ровно один.
   */
  const ensureRecvTransport = useCallback(() => {
    if (recvTransportRef.current) return Promise.resolve(recvTransportRef.current);
    if (recvTransportPromiseRef.current) return recvTransportPromiseRef.current;

    const promise = (async () => {
      console.log('[WEBRTC] requesting recvTransport from server');
      sendRef.current({ type: 'createWebRtcTransport', payload: { direction: 'recv' } });

      let recvParams = null;
      await wait((msg) => {
        if (msg.type === 'transportCreated') {
          const data = msg.payload || msg.data || {};
          if (data.direction && data.direction !== 'recv') return false;
          recvParams = {
            transportId: data.id || '',
            iceParameters: data.iceParameters || {},
            iceCandidates: data.iceCandidates || [],
            dtlsParameters: data.dtlsParameters || {},
          };
          console.log('[WEBRTC] recvTransport server params received, transportId:', data.id,
            'iceCandidates:', recvParams.iceCandidates.length);
          return true;
        }
        if (msg.type === 'error') {
          throw new Error((msg.payload || {}).message || msg.error || 'createWebRtcTransport (recv) failed');
        }
        return false;
      }, 15000);

      if (!recvParams.iceCandidates.length) {
        throw new Error('recvTransport has no ICE candidates — SFU ANNOUNCED_IP is probably unset');
      }

      const recvTransport = deviceRef.current.createRecvTransport({
        id: recvParams.transportId,
        iceParameters: recvParams.iceParameters,
        iceCandidates: recvParams.iceCandidates,
        dtlsParameters: recvParams.dtlsParameters,
        appData: { direction: 'recv' },
      });

      console.log('[WEBRTC] recvTransport created on client, id:', recvTransport.id);

      recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
        console.log('[WEBRTC] recvTransport connect event fired');
        sendRef.current({
          type: 'connectWebRtcTransport',
          payload: { transportId: recvParams.transportId, dtlsParameters },
        });

        wait((msg) => {
          if (msg.type === 'transportConnected') {
            console.log('[WEBRTC] recvTransport connected');
            callback();
            return true;
          }
          if (msg.type === 'error') {
            errback(new Error((msg.payload || {}).message || msg.error || 'connectWebRtcTransport (recv) failed'));
            return true;
          }
          return false;
        }, 15000).catch(errback);
      });

      recvTransport.on('connectionstatechange', (state) => {
        console.log('[WEBRTC] recvTransport connectionstatechange:', state);
        if (state === 'failed') {
          onSignalingRef.current?.('error', {
            message: 'ICE не установился (recv) — проверьте ANNOUNCED_IP и проброс UDP-портов SFU',
          });
        }
      });

      recvTransportRef.current = recvTransport;
      return recvTransport;
    })();

    recvTransportPromiseRef.current = promise;
    promise.catch(() => { recvTransportPromiseRef.current = null; });
    return promise;
  }, []);

  /**
   * Подписаться на удалённого producer'а (consumer side).
   * Работает и для «говорящих», и для чистых слушателей.
   */
  const subscribeToProducer = useCallback(async (remoteProducerId, userId) => {
    console.log('[WEBRTC] === SUBSCRIBE TO PRODUCER START === remoteProducerId:', remoteProducerId, 'userId:', userId);
    if (!deviceRef.current) throw new Error('Device not ready');

    const recvTransport = await ensureRecvTransport();

    console.log('[WEBRTC] requesting subscribe for producerId:', remoteProducerId);
    sendRef.current({ type: 'subscribe', payload: { producerId: remoteProducerId } });

    let consumerInfo = null;
    await wait((msg) => {
      if (msg.type === 'consumer') {
        const data = msg.payload || msg.data;
        // Subscribes run in parallel, so only accept the reply for *this*
        // producer — otherwise two consumers get swapped.
        if (!data || (data.producerId && data.producerId !== remoteProducerId)) return false;
        consumerInfo = data;
        console.log('[WEBRTC] consumer info received from server:', JSON.stringify(consumerInfo).slice(0, 300));
        return true;
      }
      if (msg.type === 'producerGone') {
        const data = msg.payload || msg.data || {};
        if (data.producerId && data.producerId !== remoteProducerId) return false;
        throw new Error(`producer ${remoteProducerId} is gone`);
      }
      if (msg.type === 'error') {
        const detail = JSON.stringify(msg.payload || {}).slice(0, 300);
        throw new Error((msg.payload || {}).message || msg.error || `subscribe failed (${detail})`);
      }
      return false;
    }, 15000);

    if (!consumerInfo.rtpParameters) {
      throw new Error(`consumer for ${remoteProducerId} arrived without rtpParameters`);
    }

    console.log('[WEBRTC] calling recvTransport.consume()');
    const consumer = await recvTransport.consume({
      id: consumerInfo.consumerId,
      producerId: consumerInfo.producerId || remoteProducerId,
      kind: consumerInfo.kind,
      rtpParameters: consumerInfo.rtpParameters,
    });

    consumersRef.current.set(consumer.id, consumer);
    console.log('[WEBRTC] consumer created successfully, id:', consumer.id, 'kind:', consumer.kind);

    // The SFU creates consumers paused so the first key frame is not wasted on
    // a receiver that does not exist yet. Now that it does, unpause.
    sendRef.current({ type: 'resumeConsumer', payload: { consumerId: consumer.id } });

    if (onSignalingRef.current) {
      onSignalingRef.current('subscribed', {
        consumerId: consumer.id,
        producerId: remoteProducerId,
        track: consumer.track,
        kind: consumer.kind,
        userId: userId || consumerInfo.userId,
      });
    }
  }, [ensureRecvTransport]);

  const cleanup = useCallback(() => {
    setDeviceReady(false);
    producersRef.current.forEach((p) => {
      if (p && typeof p.close === 'function') {
        try { p.close(); } catch (_) {}
      }
    });
    producersRef.current.clear();
    consumersRef.current.forEach((c) => {
      if (c && typeof c.close === 'function') {
        try { c.close(); } catch (_) {}
      }
    });
    consumersRef.current.clear();

    if (sendTransportRef.current) {
      try { sendTransportRef.current.close(); } catch (_) {}
      sendTransportRef.current = null;
    }
    sendTransportPromiseRef.current = null;
    if (recvTransportRef.current) {
      try { recvTransportRef.current.close(); } catch (_) {}
      recvTransportRef.current = null;
    }
    recvTransportPromiseRef.current = null;
    deviceRef.current = null;
    setLocalProducerId(null);
  }, []);

  // Инициализация: создаём Device при подключении WebSocket.
  // sendTransport создаётся по требованию через ensureSendTransport.
  useEffect(() => {
    console.log('[WEBRTC] init effect fired — wsConnected:', wsConnected);
    if (!wsConnected) {
      cleanup();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        console.log('[WEBRTC] calling setupDeviceAndTransports');
        await setupDeviceAndTransports();
        console.log('[WEBRTC] setupDeviceAndTransports completed');
      } catch (err) {
        if (!cancelled) {
          console.error('[WEBRTC] init failed:', err.message);
          onSignalingRef.current?.('error', { message: `WebRTC init failed: ${err.message}` });
        }
      }
    })();

    return () => {
      cancelled = true;
      console.log('[WEBRTC] cleanup effect running');
      cleanup();
    };
  }, [wsConnected, setupDeviceAndTransports, cleanup]);

  // При готовности Device и наличии localStream — вызываем produce.
  useEffect(() => {
    if (deviceReady && localStream) {
      console.log('[WEBRTC] deviceReady and localStream available — calling produce');
      produce(localStream).catch((err) => {
        console.error('[WEBRTC] produce failed:', err);
      });
    }
  }, [deviceReady, localStream, produce]);

  return { localProducerId, subscribeToProducer, signal };
}